// Effect-state buff (op 0x9093 / EventId 10) 的共用定義。
//
// 這類封包不帶 CCId，只代表一次「啟動脈衝（receipt）」，而非持續的 on/off 狀態。
// 因此前端把它合成成一個固定時長的 condition（synthetic CCId），套到隊伍 PC 身上，
// 直接複用既有的 condition timeline / snapshot / hydrate 機制。
//
// 本模組刻意不引入任何 Vue / data 依賴，讓 Web Worker（pureEventActor）與
// Vue 端（eventActor）都能 import。

/**
 * 樂譜卷軸的裝備欄位（PocketType）。演奏 buff（effect-state）觸發時，
 * 施法者此欄位的物品即為當下使用的樂譜（有效樂譜才會產生 effect-state）。
 * ⚠️ 值 14 來自 TW 封包觀察，非官方常數。
 */
export const SCORE_POCKET = 14;

/** 合成 CC 的基底；>= 此值者視為合成（非伺服器真實 CCId）。 */
export const SYNTHETIC_CC_BASE = 9_000_000;
/** effect-state buff 專用基底，CCId = base + subType。 */
export const EFFECT_STATE_CC_BASE = 9_100_000;

export type EffectStateBuffDef = {
    /** 封包 SubType（伺服器端 effect 類型）。 */
    subType: number;
    /** 合成出來的 CCId。 */
    ccId: number;
    /** 合成 buff 的固定時長（秒）。 */
    durationSec: number;
    /** fallback 顯示名稱（正式名稱由 condNameMap 提供）。 */
    name: string;
    /** 圖示借用的 skillId（condition image 缺圖，改用技能圖）。 */
    iconSkillId: number;
    /** true 時對全隊伍在場 PC 廣播（封包未帶接收者清單時）。 */
    broadcastToParty: boolean;
    /** true 時 Value === 0 視為停止脈衝，不套用。 */
    ignoreZeroValue: boolean;
};

// ⚠️ subType / iconSkillId 來自對 dilmatulgi 部署版前端的逆向；
//    若日後抓到真實封包，請依實況校正。
export const EFFECT_STATE_BUFFS: EffectStateBuffDef[] = [
    {
        subType: 21,
        ccId: EFFECT_STATE_CC_BASE + 21, // 9_100_021
        durationSec: 20,
        name: "樂器演奏",
        iconSkillId: 10003,
        broadcastToParty: true,
        ignoreZeroValue: true,
    },
];

export const effectStateBuffBySubType = new Map<number, EffectStateBuffDef>(
    EFFECT_STATE_BUFFS.map((d) => [d.subType, d]),
);
export const effectStateBuffByCcId = new Map<number, EffectStateBuffDef>(
    EFFECT_STATE_BUFFS.map((d) => [d.ccId, d]),
);

/** 是否為合成 CC（effect-state 等，非伺服器真實 condition）。 */
export const isSyntheticCc = (ccId: number): boolean =>
    ccId >= SYNTHETIC_CC_BASE;

/**
 * 合成 CC 的圖示 URL：characterconditionimage 沒有對應圖，改用 iconSkillId 的技能圖。
 * 非合成 CC 回傳 null（由呼叫端走預設 characterconditionimage 路徑）。
 */
export const effectStateCondIconUrl = (
    region: string,
    ccId: number,
): string | null => {
    const def = effectStateBuffByCcId.get(ccId);
    if (!def) {
        return null;
    }
    return `https://cdn.jsdelivr.net/gh/eldisa/mabinogiImage@main/SkillImage/${def.iconSkillId}.png`
};

/**
 * condition 圖示 URL 的統一入口：合成 buff 走技能圖，其餘走預設 characterconditionimage。
 * 所有顯示 condition icon 的地方都應改用本函式，避免合成 CC 破圖。
 */
export const condIconUrl = (region: string, ccId: number): string =>
    effectStateCondIconUrl(region, ccId) ??
    `https://cdn.jsdelivr.net/gh/eldisa/mabinogiImage@main/ConditionImage/${ccId}.png`;

